/**
 * OTIF Settings Tab Component
 * Handles OTIF calculation settings, tolerance windows, supplier overrides, and score mappings
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  Loader2,
  RotateCcw,
  TrendingUp,
  Target,
  Clock,
  AlertTriangle,
  Info,
  Pencil,
  X,
  Check,
  Settings2,
  ChevronDown,
  ChevronUp,
  Search,
  Users,
} from 'lucide-react';
import {
  SupplierPerformanceSettings,
} from '../../../lib/api';
import { PerformanceFormData, SupplierOTIFData, SupplierEditData } from './types';
import { useAuth } from '../../../contexts/AuthContext';
import { OTIFGradeThresholdsSection } from './OTIFGradeThresholdsSection';
import { useOTIFGradeThresholds } from './useOTIFGradeThresholds';

interface OTIFSettingsTabProps {
  settings: SupplierPerformanceSettings | null;
  formData: PerformanceFormData;
  /** CR-6267: OTIF colour band state, owned by useOTIFGradeThresholds */
  otifGrades: ReturnType<typeof useOTIFGradeThresholds>;
  showSupplierOverrides: boolean;
  supplierSearch: string;
  editingSupplier: number | null;
  supplierEditData: SupplierEditData | null;
  savingSupplier: boolean;
  onInputChange: (field: string, value: string | number | boolean) => void;
  onResetOTIFMappings: () => void;
  onSetShowSupplierOverrides: (show: boolean) => void;
  onSetSupplierSearch: (search: string) => void;
  onSetSupplierEditData: (data: SupplierEditData | null) => void;
  onEditSupplier: (supplier: SupplierOTIFData) => void;
  onCancelEditSupplier: () => void;
  onSaveSupplier: (supplierId: number) => void;
  onToggleSupplierCustom: (supplier: SupplierOTIFData) => void;
}

export function OTIFSettingsTab({
  settings,
  formData,
  otifGrades,
  showSupplierOverrides,
  supplierSearch,
  editingSupplier,
  supplierEditData,
  savingSupplier,
  onInputChange,
  onResetOTIFMappings,
  onSetShowSupplierOverrides,
  onSetSupplierSearch,
  onSetSupplierEditData,
  onEditSupplier,
  onCancelEditSupplier,
  onSaveSupplier,
  onToggleSupplierCustom,
}: OTIFSettingsTabProps) {
  // CR-6543 r3: the fallback firm window is a staff-only setting — visible
  // to admins, editable only when the Django user carries is_staff /
  // is_superuser (the backend enforces the same rule on PATCH).
  const { user } = useAuth();
  const canEditFirmDays = Boolean(user?.is_staff || user?.is_superuser);
  return (
    <div className="space-y-4">
      {/* Delivery Metric Mode */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            <CardTitle>Delivery Metric Mode</CardTitle>
          </div>
          <CardDescription>
            Choose which delivery performance metrics to calculate and display across the platform.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {([
              { value: 'both', label: 'Both OTIF and OTD', desc: 'Calculate and display both On-Time In-Full and On-Time Delivery metrics' },
              { value: 'otif_only', label: 'OTIF Only', desc: 'Only calculate and display On-Time In-Full. OTD charts and KPIs will be hidden.' },
              { value: 'otd_only', label: 'OTD Only', desc: 'Only calculate and display On-Time Delivery. OTIF charts and KPIs will be hidden.' },
            ] as const).map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.delivery_metric_mode === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <input
                  type="radio"
                  name="delivery_metric_mode"
                  value={option.value}
                  checked={formData.delivery_metric_mode === option.value}
                  onChange={() => onInputChange('delivery_metric_mode', option.value)}
                  className="h-4 w-4"
                />
                <div>
                  <span className="font-medium">{option.label}</span>
                  <p className="text-sm text-muted-foreground">{option.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPI Target Values */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <CardTitle>KPI Target Values</CardTitle>
          </div>
          <CardDescription>
            Set target values for OTIF and OTD. These targets appear as goal lines on dashboard graphs and KPI indicators.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {(formData.delivery_metric_mode === 'both' || formData.delivery_metric_mode === 'otif_only') && (
              <div className="space-y-2">
                <Label htmlFor="otif_target" className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-green-600" />
                  OTIF Target
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="otif_target"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.otif_target}
                    onChange={(e) => onInputChange('otif_target', e.target.value)}
                  />
                  <span>%</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Target percentage shown as goal line on OTIF charts
                </p>
              </div>
            )}
            {(formData.delivery_metric_mode === 'both' || formData.delivery_metric_mode === 'otd_only') && (
              <div className="space-y-2">
                <Label htmlFor="otd_target" className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-blue-600" />
                  OTD Target
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="otd_target"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.otd_target}
                    onChange={(e) => onInputChange('otd_target', e.target.value)}
                  />
                  <span>%</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Target percentage shown as goal line on OTD charts
                </p>
              </div>
            )}
          </div>
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4" />
              <span className="font-medium">Target vs. Threshold</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target values set the goal line on charts. Color thresholds below determine the green/yellow/red color coding of actual performance values.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle>OTIF Calculation Settings</CardTitle>
          </div>
          <CardDescription>
            Configure how On-Time In-Full (OTIF) is calculated for supplier deliveries.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* OTIF Tolerance Window */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Delivery Tolerance Window
            </h3>
            <p className="text-sm text-muted-foreground">
              Define the acceptable delivery window for a delivery to count as "on time".
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="otif_days_early_allowed">Days Early Allowed</Label>
                <Input
                  id="otif_days_early_allowed"
                  type="number"
                  min="0"
                  value={formData.otif_days_early_allowed}
                  onChange={(e) => onInputChange('otif_days_early_allowed', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Deliveries up to {formData.otif_days_early_allowed} days early will count as OTIF
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otif_days_late_allowed">Days Late Allowed</Label>
                <Input
                  id="otif_days_late_allowed"
                  type="number"
                  min="0"
                  value={formData.otif_days_late_allowed}
                  onChange={(e) => onInputChange('otif_days_late_allowed', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Deliveries up to {formData.otif_days_late_allowed} day(s) late will count as OTIF
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="arrears_buffer_days">Arrears Buffer (days)</Label>
                <Input
                  id="arrears_buffer_days"
                  type="number"
                  min="0"
                  value={formData.arrears_buffer_days}
                  onChange={(e) => onInputChange('arrears_buffer_days', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  A schedule is only counted as arrears once it is more than {formData.arrears_buffer_days} day(s) past due. Applies to the dashboard &amp; forecast arrears.
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="default_firm_days">Default Firm Window (days)</Label>
                  <Badge variant="outline" className="text-[10px] uppercase">Staff only</Badge>
                </div>
                <Input
                  id="default_firm_days"
                  type="number"
                  min="0"
                  max="365"
                  value={formData.default_firm_days}
                  disabled={!canEditFirmDays}
                  onChange={(e) => onInputChange('default_firm_days', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Schedule lines for parts without a configured firm window are treated as firm for the next {formData.default_firm_days} day(s); beyond that they count as forecast. Drives the Scheduled Dashboard, forecast export and schedule list splits.
                </p>
              </div>
            </div>
          </div>

          {/* OTIF Override Stats (CR-3375) */}
          {settings?.supplier_otif_stats && settings.supplier_otif_stats.total_suppliers > 0 && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Supplier OTIF Window Usage
              </h3>
              <p className="text-sm text-muted-foreground">
                Shows how many suppliers are using the default OTIF tolerance window vs. custom per-supplier overrides.
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <div className="text-2xl font-bold">{settings.supplier_otif_stats.total_suppliers}</div>
                  <div className="text-sm text-muted-foreground">Total Suppliers</div>
                </div>
                <div className="p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg text-center">
                  <div className="text-2xl font-bold">
                    {settings.supplier_otif_stats.using_default}
                  </div>
                  <div className="text-sm font-semibold">Using Default Window</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    ({formData.otif_days_early_allowed} days early, {formData.otif_days_late_allowed} day late)
                  </div>
                </div>
                <div className="p-4 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg text-center">
                  <div className="text-2xl font-bold">
                    {settings.supplier_otif_stats.using_custom}
                  </div>
                  <div className="text-sm font-semibold">Using Custom Window</div>
                </div>
              </div>

              {/* Manage Supplier Overrides Button */}
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => onSetShowSupplierOverrides(!showSupplierOverrides)}
                  className="w-full justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Settings2 className="h-4 w-4" />
                    Manage Supplier OTIF Overrides
                  </span>
                  {showSupplierOverrides ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Supplier Override Management Panel */}
              {showSupplierOverrides && settings.supplier_otif_stats.all_suppliers && (
                <SupplierOverridePanel
                  suppliers={settings.supplier_otif_stats.all_suppliers}
                  supplierSearch={supplierSearch}
                  editingSupplier={editingSupplier}
                  supplierEditData={supplierEditData}
                  savingSupplier={savingSupplier}
                  onSetSupplierSearch={onSetSupplierSearch}
                  onSetSupplierEditData={onSetSupplierEditData}
                  onEditSupplier={onEditSupplier}
                  onCancelEditSupplier={onCancelEditSupplier}
                  onSaveSupplier={onSaveSupplier}
                  onToggleSupplierCustom={onToggleSupplierCustom}
                />
              )}
            </div>
          )}

          {/* Arrears Setting */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Arrears Handling
            </h3>
            {/* DORMANT — not wired; see METRIC-DEFINITIONS W3-T2 (wave 3a). The
                "Include Arrears in OTIF" control is retired: arrears are always
                in the denominator, counted once. The column stays; nothing reads it. */}
            <p className="text-sm text-muted-foreground">
              Overdue open PO lines (arrears) are always counted in the OTIF denominator, once each.
            </p>
          </div>

          {/* OTIF Grade Thresholds (CR-6267) */}
          <OTIFGradeThresholdsSection
            bands={settings?.otif_grade_thresholds ?? []}
            editingBand={otifGrades.editingBand}
            bandEditData={otifGrades.bandEditData}
            savingBand={otifGrades.savingBand}
            showAddBand={otifGrades.showAddBand}
            newBandData={otifGrades.newBandData}
            addingBand={otifGrades.addingBand}
            resettingBands={otifGrades.resettingBands}
            onSetShowAddBand={otifGrades.setShowAddBand}
            onResetBands={otifGrades.handleResetBands}
            onEditBand={otifGrades.handleEditBand}
            onCancelEditBand={otifGrades.handleCancelEditBand}
            onSaveBand={otifGrades.handleSaveBand}
            onBandFieldChange={otifGrades.handleBandFieldChange}
            onAddBand={otifGrades.handleAddBand}
            onDeleteBand={otifGrades.handleDeleteBand}
            onNewBandFieldChange={otifGrades.handleNewBandFieldChange}
          />

          {/* OTIF Formula Info */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span className="font-medium">OTIF Calculation Formula</span>
            </div>
            <code className="block text-sm p-2 bg-background rounded border">
              OTIF % = Passed PO Lines / (Closed PO Lines + Arrears) x 100
            </code>
            <p className="text-sm text-muted-foreground">
              A PO line is marked as OTIF=1 when the delivery is received within the tolerance window
              ({formData.otif_days_early_allowed} days early to {formData.otif_days_late_allowed} day(s) late) and the full quantity is delivered.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* OTIF Score Mappings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>OTIF Score Mappings</CardTitle>
              <CardDescription>
                Map OTIF percentages to score points for rating calculations.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onResetOTIFMappings}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {settings?.otif_score_mappings && settings.otif_score_mappings.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground pb-2 border-b">
                <div>Min OTIF %</div>
                <div>Max OTIF %</div>
                <div>Score Points</div>
                <div></div>
              </div>
              {settings.otif_score_mappings.map((mapping) => (
                <div key={mapping.id} className="grid grid-cols-4 gap-4 items-center py-2">
                  <div>{mapping.min_otif_percent}%</div>
                  <div>{mapping.max_otif_percent}%</div>
                  <div className="font-medium">{mapping.mapped_score} pts</div>
                  <div>
                    <Badge variant="outline">
                      {parseFloat(mapping.min_otif_percent) >= 95 ? 'Excellent' :
                       parseFloat(mapping.min_otif_percent) >= 85 ? 'Good' :
                       parseFloat(mapping.min_otif_percent) >= 70 ? 'Fair' : 'Poor'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No OTIF score mappings configured. Click "Reset to Defaults" to create default mappings.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Supplier Override Panel Sub-component
interface SupplierOverridePanelProps {
  suppliers: SupplierOTIFData[];
  supplierSearch: string;
  editingSupplier: number | null;
  supplierEditData: SupplierEditData | null;
  savingSupplier: boolean;
  onSetSupplierSearch: (search: string) => void;
  onSetSupplierEditData: (data: SupplierEditData | null) => void;
  onEditSupplier: (supplier: SupplierOTIFData) => void;
  onCancelEditSupplier: () => void;
  onSaveSupplier: (supplierId: number) => void;
  onToggleSupplierCustom: (supplier: SupplierOTIFData) => void;
}

function SupplierOverridePanel({
  suppliers,
  supplierSearch,
  editingSupplier,
  supplierEditData,
  savingSupplier,
  onSetSupplierSearch,
  onSetSupplierEditData,
  onEditSupplier,
  onCancelEditSupplier,
  onSaveSupplier,
  onToggleSupplierCustom,
}: SupplierOverridePanelProps) {
  const filteredSuppliers = suppliers.filter((supplier) =>
    supplier.company__name.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  return (
    <div className="mt-4 border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          All Suppliers ({suppliers.length})
        </h4>
        <p className="text-sm text-muted-foreground">
          Toggle custom OTIF windows or click Edit to set specific values
        </p>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search suppliers..."
          value={supplierSearch}
          onChange={(e) => onSetSupplierSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="max-h-96 overflow-y-auto space-y-2">
        {filteredSuppliers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No suppliers found matching "{supplierSearch}"</p>
          </div>
        ) : (
          filteredSuppliers.map((supplier) => (
            <div
              key={supplier.id}
              className={`p-3 rounded-lg border ${
                supplier.use_custom_otif_window
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  : 'bg-muted/30 border-transparent'
              }`}
            >
              {editingSupplier === supplier.id && supplierEditData ? (
                // Edit Mode
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{supplier.company__name}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onCancelEditSupplier}
                        disabled={savingSupplier}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onSaveSupplier(supplier.id)}
                        disabled={savingSupplier}
                      >
                        {savingSupplier ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={supplierEditData.use_custom_otif_window}
                        onChange={(e) =>
                          onSetSupplierEditData({
                            ...supplierEditData,
                            use_custom_otif_window: e.target.checked,
                          })
                        }
                        className="h-4 w-4"
                      />
                      Use Custom Window
                    </label>
                  </div>
                  {supplierEditData.use_custom_otif_window && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Days Early Allowed</Label>
                        <Input
                          type="number"
                          min="0"
                          value={supplierEditData.otif_days_early_allowed}
                          onChange={(e) =>
                            onSetSupplierEditData({
                              ...supplierEditData,
                              otif_days_early_allowed: parseInt(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Days Late Allowed</Label>
                        <Input
                          type="number"
                          min="0"
                          value={supplierEditData.otif_days_late_allowed}
                          onChange={(e) =>
                            onSetSupplierEditData({
                              ...supplierEditData,
                              otif_days_late_allowed: parseInt(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // View Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={supplier.use_custom_otif_window}
                      onChange={() => onToggleSupplierCustom(supplier)}
                      disabled={savingSupplier}
                      className="h-4 w-4"
                    />
                    <span className="font-medium">{supplier.company__name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {supplier.use_custom_otif_window ? (
                      <Badge variant="default" className="bg-blue-600">
                        {supplier.otif_days_early_allowed}d early / {supplier.otif_days_late_allowed}d late
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Default</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditSupplier(supplier)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
